'use client';

import { useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  LuFlame,
  LuPackage,
  LuShield,
  LuMail,
  LuPhone,
  LuCalendar,
  LuLoader,
  LuCheck,
  LuBike,
  LuArrowRight,
  LuMapPin,
} from 'react-icons/lu';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/toaster';
import { Card, CardContent } from '@/components/ui/card';
import { useServiceForm } from '@/hooks/useServiceForm';
import { ServiceType } from '@prisma/client';
import { useUser } from '@clerk/nextjs';

const ServiceSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  email: Yup.string().email('Invalid email').required('Required'),
  phone: Yup.string().nullable(),
  serviceType: Yup.string().required('Required'),
  message: Yup.string(),
  preferredDate: Yup.date()
    .required('Required')
    .min(new Date(), 'Date cannot be in the past'),
});

const QwetuGasSupplyPage = () => {
  const { user, isSignedIn } = useUser();
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [activeService, setActiveService] = useState<number | null>(null);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(
    null
  );
  const { isSubmitting, submitServiceRequest } = useServiceForm();

  const services = [
    {
      icon: LuFlame,
      title: 'Cylinder Refill',
      description:
        'Same-day refill for K-Gas, Sea Gas, Pro Gas, Afrigas, Taifa Gas, and more',
      features: [
        '6 kg and 13 kg cylinders',
        'All major brands',
        'Quality-checked seals',
        'Receipt with every refill',
      ],
      image: '/images/pages/qwetu-gas/products-collage.jpg',
      overlayColor: 'from-orange-600/80',
    },
    {
      icon: LuPackage,
      title: 'New Cylinder Kits',
      description: 'Brand-new cylinders bundled with hose, regulator, and clips',
      features: [
        'Full starter kit',
        'Genuine accessories',
        'Pressure-tested',
        'Warranty-backed brands',
      ],
      image: '/images/pages/qwetu-gas/storefront.jpg',
      overlayColor: 'from-blue-600/80',
    },
    {
      icon: LuShield,
      title: 'Stoves & Accessories',
      description:
        'Burners, two-burner stoves, regulators, hose kits, LED bulbs, and charging cables',
      features: [
        'Burner heads',
        'Two-burner gas cookers',
        'Regulators and hoses',
        'Small electronics',
      ],
      image: '/images/pages/qwetu-gas/shopfront-wide.jpg',
      overlayColor: 'from-emerald-600/80',
    },
    {
      icon: LuBike,
      title: 'Free Home Delivery',
      description:
        'We bring the cylinder to your door at no extra charge anywhere in Kaoyeni',
      features: [
        'Same-day delivery',
        'No delivery fee',
        'Cash or M-Pesa on arrival',
        'Friendly riders',
      ],
      image: '/images/pages/qwetu-gas/delivery-rider.jpg',
      overlayColor: 'from-amber-600/80',
    },
  ];

  interface FormValues {
    name: string;
    email: string;
    phone: string;
    serviceType: string;
    cylinderBrand?: string;
    cylinderSize?: string;
    message: string;
    preferredDate: string;
  }

  const handleSubmit = async (
    values: FormValues,
    { resetForm }: { resetForm: () => void }
  ) => {
    setSubmitStatus(null);

    try {
      toast.loading('Submitting your request...');

      const preferredDateTime = new Date(values.preferredDate);
      const formattedDate = preferredDateTime.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      const formattedTime = preferredDateTime.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      });

      const detailedMessage = `
Cylinder Brand: ${values.cylinderBrand || 'Not specified'}
Cylinder Size: ${values.cylinderSize || 'Not specified'}
Preferred Date: ${formattedDate}
Preferred Time: ${formattedTime}
Additional information: ${values.message || 'None provided'}
      `.trim();

      const serviceData = {
        name: values.name,
        email: values.email,
        phone: values.phone || '',
        message: detailedMessage,
        serviceType: ServiceType.GAS_SUPPLY,
        preferredDate: new Date(values.preferredDate),
        specificService: values.serviceType,
        cylinderBrand: values.cylinderBrand,
        cylinderSize: values.cylinderSize,
        serviceColor: 'orange',
        preferredTime: formattedTime,
      };

      const result = await submitServiceRequest(serviceData);
      toast.dismiss();

      if (result.success) {
        setSubmitStatus('success');
        toast.success('Gas supply request submitted successfully!');
        resetForm();
        setSelectedService(null);
      } else {
        setSubmitStatus('error');
        toast.error(
          result.error || 'Failed to submit request. Please try again.'
        );
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
      toast.dismiss();
      toast.error('Failed to submit request. Please try again.');
    }
  };

  return (
    <div className='min-h-screen bg-[#0a192f]'>
      <Toaster position='top-center' />
      {/* Hero Section */}
      <div className='relative h-[40vh] md:h-[60vh] overflow-hidden'>
        <div className='absolute inset-0'>
          <img
            src='/images/pages/qwetu-gas/storefront.jpg'
            alt='Qwetu Gas Supplier storefront'
            className='h-full w-full object-cover'
          />
          <div className='absolute inset-0 bg-linear-to-b from-[#0a192f]/50 via-[#0a192f]/80 to-[#0a192f]' />
        </div>
        <div className='relative h-full mx-auto max-w-7xl px-4'>
          <div className='flex h-full flex-col items-center justify-center text-center'>
            <h1 className='text-3xl md:text-4xl lg:text-6xl font-bold text-white'>
              Qwetu Gas Supplier
            </h1>
            <p className='mt-3 md:mt-6 text-base md:text-xl text-orange-100'>
              Unachokithamini, Tunakidhamini &mdash; free delivery, every brand,
              every size
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className='relative mx-auto max-w-7xl px-4 py-8 md:py-16'>
        {/* Contact Info Cards */}
        <div className='grid gap-3 md:gap-4 grid-cols-1 sm:grid-cols-3 mb-8 md:mb-16'>
          {[
            {
              icon: LuPhone,
              label: 'Call us',
              value: '0118 066 604 / 0706 185 672',
              gradient: 'from-orange-400 to-amber-500',
            },
            {
              icon: LuMapPin,
              label: 'Location',
              value: 'Kaoyeni - Kwa Mogaka',
              gradient: 'from-amber-400 to-orange-500',
            },
            {
              icon: LuCalendar,
              label: 'Hours',
              value: 'Mon-Sun: 7am - 8pm',
              gradient: 'from-emerald-400 to-teal-500',
            },
          ].map((contact, index) => (
            <div
              key={index}
              className='group relative overflow-hidden rounded-xl bg-linear-to-br from-orange-900/40 to-amber-950/40 backdrop-blur-xs p-3 md:p-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-orange-500/15 border border-orange-500/20 hover:border-orange-500/40'
            >
              <div className='flex items-center gap-3 md:gap-4'>
                <div
                  className={`rounded-xl bg-linear-to-br ${contact.gradient} p-2 md:p-3 text-white transition-transform duration-300 group-hover:scale-110 shadow-md`}
                >
                  <contact.icon className='h-4 w-4 md:h-6 md:w-6' />
                </div>
                <div>
                  <p className='text-xs md:text-sm text-orange-300/80 font-medium'>
                    {contact.label}
                  </p>
                  <p className='text-sm md:text-lg font-semibold text-white'>
                    {contact.value}
                  </p>
                </div>
              </div>
              <div className='absolute bottom-0 left-0 w-0 h-0.5 bg-linear-to-r from-transparent via-orange-400 to-transparent group-hover:w-full transition-all duration-700'></div>
            </div>
          ))}
        </div>

        {/* Services Grid */}
        <div className='grid gap-4 md:gap-6 grid-cols-1 md:grid-cols-2 mb-8 md:mb-16'>
          {services.map((service, idx) => (
            <div
              key={idx}
              className='group relative isolate cursor-pointer transform transition-all duration-500 hover:scale-[1.02]'
              onMouseEnter={() => setActiveService(idx)}
              onMouseLeave={() => setActiveService(null)}
              onClick={() => setSelectedService(service.title)}
            >
              <div className='relative h-[250px] md:h-[400px] overflow-hidden rounded-2xl md:rounded-3xl transition-all duration-500 shadow-xl shadow-orange-500/20 border border-transparent hover:border-orange-500/30'>
                <img
                  src={service.image}
                  alt={service.title}
                  className='absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-110'
                />

                <div
                  className={`absolute inset-0 bg-linear-to-b ${service.overlayColor} to-[#0a192f]/90 transition-opacity duration-500
                  ${activeService === idx ? 'opacity-90' : 'opacity-80'}`}
                />

                <div className='absolute top-0 left-0 right-0 h-2 bg-linear-to-r from-orange-400 to-amber-500'></div>

                <div className='relative h-full p-4 md:p-8 flex flex-col justify-end'>
                  <div className='absolute top-8 right-8 w-24 h-24 rounded-full bg-linear-to-br from-orange-400/10 to-transparent blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-700'></div>

                  <div className='mb-3 md:mb-6'>
                    <h3 className='text-xl md:text-2xl font-bold text-white mb-2 md:mb-4 transform transition-transform duration-500 group-hover:-translate-y-2'>
                      <span className='bg-linear-to-r from-orange-300 to-amber-400 bg-clip-text text-transparent drop-shadow-md'>
                        {service.title}
                      </span>
                    </h3>
                    <p className='text-sm md:text-base text-gray-200 transform transition-all duration-500 group-hover:-translate-y-2 opacity-80 group-hover:opacity-100'>
                      {service.description}
                    </p>
                  </div>

                  <div className='w-0 h-[2px] bg-linear-to-r from-white/0 via-orange-400/70 to-white/0 group-hover:w-full transition-all duration-700 mb-3 md:mb-4'></div>

                  <ul className='space-y-1 md:space-y-2 transform transition-all duration-500 translate-y-8 opacity-0 group-hover:translate-y-0 group-hover:opacity-100'>
                    {service.features.map((feature, i) => (
                      <li
                        key={i}
                        className='flex items-center space-x-2 group/item'
                      >
                        <span className='shrink-0 rounded-full bg-orange-500/20 p-1 md:p-1.5 shadow-lg shadow-orange-500/20 transition-transform duration-300 group-hover/item:scale-110'>
                          <LuCheck className='h-3 w-3 md:h-4 md:w-4 text-orange-300' />
                        </span>
                        <span className='text-xs md:text-sm text-orange-100'>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Service Request Form */}
        <div className='mx-auto max-w-4xl'>
          <Card className='overflow-hidden rounded-lg sm:rounded-xl md:rounded-3xl bg-linear-to-br from-orange-950/90 to-amber-900/90 backdrop-blur-xs shadow-xl border border-orange-500/20'>
            <CardContent className='p-3 sm:p-4 md:p-8'>
              <div className='mb-3 sm:mb-6 md:mb-8 text-center'>
                <h2 className='text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-white'>
                  <span className='bg-linear-to-r from-orange-400 via-amber-400 to-yellow-400 bg-clip-text text-transparent'>
                    Request Gas Supply
                  </span>
                </h2>
                <p className='mt-1 sm:mt-2 text-xs sm:text-sm md:text-base text-orange-300'>
                  Tell us what you need and we'll deliver it straight to your
                  door
                </p>
              </div>

              {selectedService ? (
                <Formik
                  initialValues={{
                    name: isSignedIn && user?.fullName ? user.fullName : '',
                    email:
                      isSignedIn && user?.primaryEmailAddress?.emailAddress
                        ? user.primaryEmailAddress.emailAddress
                        : '',
                    phone: '',
                    serviceType: selectedService || '',
                    cylinderBrand: '',
                    cylinderSize: '',
                    message: '',
                    preferredDate: new Date().toISOString().split('T')[0],
                  }}
                  validationSchema={ServiceSchema}
                  onSubmit={handleSubmit}
                  enableReinitialize
                >
                  {({ errors, touched, isValid }) => (
                    <Form className='space-y-4 md:space-y-6'>
                      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6'>
                        <div>
                          <label
                            htmlFor='name'
                            className='mb-1.5 block text-sm font-medium text-orange-200'
                          >
                            Full Name
                          </label>
                          <Field
                            name='name'
                            className='w-full rounded-lg border border-orange-600/50 bg-[#2a1808]/60 p-3 text-sm text-orange-50 placeholder-orange-400/60 focus:border-orange-500 focus:ring-3 focus:ring-orange-500/30 transition'
                            placeholder='Your Full Name'
                            readOnly={isSignedIn}
                          />
                          {errors.name && touched.name ? (
                            <div className='mt-1.5 text-xs text-red-400'>
                              {errors.name}
                            </div>
                          ) : null}
                        </div>
                        <div>
                          <label
                            htmlFor='email'
                            className='mb-1.5 block text-sm font-medium text-orange-200'
                          >
                            Email Address
                          </label>
                          <Field
                            name='email'
                            type='email'
                            className='w-full rounded-lg border border-orange-600/50 bg-[#2a1808]/60 p-3 text-sm text-orange-50 placeholder-orange-400/60 focus:border-orange-500 focus:ring-3 focus:ring-orange-500/30 transition'
                            placeholder='your.email@example.com'
                            readOnly={isSignedIn}
                          />
                          {errors.email && touched.email ? (
                            <div className='mt-1.5 text-xs text-red-400'>
                              {errors.email}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className='grid gap-4 md:gap-6 grid-cols-1 md:grid-cols-2'>
                        <div>
                          <label
                            htmlFor='phone'
                            className='mb-1 md:mb-2 block text-xs md:text-sm font-medium text-orange-300'
                          >
                            Phone Number
                          </label>
                          <Field
                            name='phone'
                            className='service-amber-input'
                            placeholder='+254 7XX XXX XXX'
                          />
                          {errors.phone && touched.phone && (
                            <div className='mt-1 text-xs md:text-sm text-red-400'>
                              {errors.phone}
                            </div>
                          )}
                        </div>

                        <div>
                          <label
                            htmlFor='serviceType'
                            className='mb-1 md:mb-2 block text-xs md:text-sm font-medium text-orange-300'
                          >
                            Service Type
                          </label>
                          <Field
                            as='select'
                            name='serviceType'
                            className='service-amber-input'
                          >
                            {services.map((service) => (
                              <option
                                key={service.title}
                                value={service.title}
                                className='bg-orange-900'
                              >
                                {service.title}
                              </option>
                            ))}
                          </Field>
                        </div>

                        <div>
                          <label
                            htmlFor='cylinderBrand'
                            className='mb-1 md:mb-2 block text-xs md:text-sm font-medium text-orange-300'
                          >
                            Cylinder Brand
                          </label>
                          <Field
                            as='select'
                            name='cylinderBrand'
                            className='service-amber-input'
                          >
                            <option value=''>Select brand</option>
                            <option value='K-Gas'>K-Gas</option>
                            <option value='Sea Gas'>Sea Gas</option>
                            <option value='Pro Gas'>Pro Gas</option>
                            <option value='Afrigas'>Afrigas</option>
                            <option value='Taifa Gas'>Taifa Gas</option>
                            <option value='Max Gas'>Max Gas</option>
                            <option value='Ola Energy'>Ola Energy</option>
                            <option value='Other'>Other / Not sure</option>
                          </Field>
                        </div>

                        <div>
                          <label
                            htmlFor='cylinderSize'
                            className='mb-1 md:mb-2 block text-xs md:text-sm font-medium text-orange-300'
                          >
                            Cylinder Size
                          </label>
                          <Field
                            as='select'
                            name='cylinderSize'
                            className='service-amber-input'
                          >
                            <option value=''>Select size</option>
                            <option value='3kg'>3 kg</option>
                            <option value='6kg'>6 kg</option>
                            <option value='13kg'>13 kg</option>
                            <option value='Other'>Other</option>
                          </Field>
                        </div>

                        <div>
                          <label
                            htmlFor='preferredDate'
                            className='mb-1 md:mb-2 block text-xs md:text-sm font-medium text-orange-300'
                          >
                            Preferred Date &amp; Time
                          </label>
                          <Field
                            name='preferredDate'
                            type='datetime-local'
                            className='service-amber-input'
                            min={new Date().toISOString().slice(0, 16)}
                          />
                          {errors.preferredDate && touched.preferredDate && (
                            <div className='mt-1 text-xs md:text-sm text-red-400'>
                              {errors.preferredDate}
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor='message'
                          className='mb-1 md:mb-2 block text-xs md:text-sm font-medium text-orange-300'
                        >
                          Delivery Notes
                        </label>
                        <Field
                          as='textarea'
                          name='message'
                          rows={3}
                          className='service-amber-input'
                          placeholder='Delivery address, landmark, gate code, or anything else our rider should know...'
                        />
                      </div>

                      <div className='flex justify-end'>
                        <button
                          type='button'
                          onClick={() => setSelectedService(null)}
                          className='mr-2 xs:mr-2.5 sm:mr-3 md:mr-4 rounded-md xs:rounded-lg border border-orange-400 px-3 xs:px-3.5 sm:px-4 md:px-6 py-1.5 xs:py-2 sm:py-2 md:py-3 text-[11px] xs:text-xs md:text-sm text-orange-300 transition-colors hover:bg-orange-900/40 min-w-[60px] xs:min-w-[70px] sm:min-w-[80px]'
                        >
                          Cancel
                        </button>
                        <button
                          type='submit'
                          disabled={isSubmitting || !isValid}
                          className='flex items-center justify-center space-x-1 md:space-x-2 rounded-md xs:rounded-lg bg-linear-to-r from-orange-600 to-amber-600 px-3 xs:px-3.5 sm:px-4 md:px-6 py-1.5 xs:py-2 sm:py-2 md:py-3 text-[11px] xs:text-xs md:text-sm text-white transition-all hover:from-orange-500 hover:to-amber-500 disabled:opacity-70 hover:shadow-lg hover:shadow-orange-500/20 hover:scale-105 min-w-[100px] xs:min-w-[120px] sm:min-w-[140px]'
                        >
                          {isSubmitting ? (
                            <>
                              <LuLoader className='h-4 w-4 md:h-5 md:w-5 animate-spin' />
                              <span>Submitting...</span>
                            </>
                          ) : (
                            <>
                              <span>Book Delivery</span>
                              <LuArrowRight className='h-4 w-4 md:h-5 md:w-5' />
                            </>
                          )}
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              ) : (
                <div className='text-center'>
                  <p className='mb-4 md:mb-6 text-sm md:text-base text-orange-300'>
                    Select a service above to start your gas supply order
                  </p>
                  <div className='grid gap-2 md:gap-4 grid-cols-2 sm:grid-cols-2 md:grid-cols-4'>
                    {services.map((service, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedService(service.title)}
                        className='group flex flex-col items-center rounded-xl bg-orange-900/30 p-3 md:p-4 transition-all duration-300 hover:bg-amber-900/40 hover:shadow-lg hover:shadow-orange-500/10 hover:scale-105 border border-orange-800/10 hover:border-orange-700/30'
                      >
                        <div className='mb-2 md:mb-3 rounded-full bg-linear-to-br from-orange-500 to-amber-500 p-2 md:p-3 text-white transition-transform duration-300 group-hover:scale-110 group-hover:shadow-md group-hover:from-orange-400 group-hover:to-amber-400'>
                          <service.icon className='h-4 w-4 md:h-6 md:w-6' />
                        </div>
                        <span className='text-xs md:text-sm font-medium text-white'>
                          {service.title}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default QwetuGasSupplyPage;
